import React, { useState } from 'react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TrendingUp,
  Users,
  DollarSign,
  Calendar as CalendarIcon,
  CalendarCheck,
  AlertCircle,
  Star,
  Filter
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useStore } from '../store/useStore';
import { useDashboardData } from '../hooks/useDashboardData';
import { ChartAreaInteractive } from '../components/dashboard/ChartAreaInteractive';
import { usePageTitle } from '../hooks/usePageTitle';
import TodaysReservations from '../components/dashboard/TodaysReservations';
import CustomerDetailDialog from '../components/dashboard/CustomerDetailDialog';

const Dashboard: React.FC = () => {
  usePageTitle('ダッシュボード');
  const {
    setCurrentPage,
    setSelectedCustomerId,
    setSalesActiveTab
  } = useStore();

  // ユーザーが選択した日付
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Force cache clear

  // Supabase統合: カスタムフックでデータ取得（選択された日付でフィルタリング）
  const {
    metrics,
    todaysReservations: supabaseTodaysReservations,
    frequentCustomers,
    recentActivities: supabaseRecentActivities,
    availableSlots,
    chartData,
    loading,
    error,
    refreshData
  } = useDashboardData();

  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [activityFilter, setActivityFilter] = useState<string>('all');
  const [showActivityFilter, setShowActivityFilter] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Today's reservations - Supabaseデータを拡張
  const todaysReservations = supabaseTodaysReservations.map((reservation) => ({
    id: reservation.id,
    time: reservation.startTime,
    customer: reservation.customerName,
    customerId: reservation.customerId,
    service: reservation.serviceName,
    staff: reservation.staffName,
    status: reservation.status,
    isVip: (frequentCustomers.find(c => c.id === reservation.customerId)?.totalVisits ?? 0) >= 20,
    visitCount: frequentCustomers.find(c => c.id === reservation.customerId)?.totalVisits ?? 1
  }));

  // Format numbers for display
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('ja-JP').format(num);

  // ========================================
  // モックデータ生成ヘルパー関数
  // ========================================
  const generateKana = (name: string): string => {
    // 簡易カタカナ変換（名前の各文字をカタカナ風に変換）
    const kanaMap: Record<string, string> = {
      '佐藤': 'サトウ', '鈴木': 'スズキ', '高橋': 'タカハシ', '田中': 'タナカ',
      '伊藤': 'イトウ', '渡辺': 'ワタナベ', '山本': 'ヤマモト', '中村': 'ナカムラ',
      '小林': 'コバヤシ', '加藤': 'カトウ', '吉田': 'ヨシダ', '山田': 'ヤマダ',
      '佐々木': 'ササキ', '山口': 'ヤマグチ', '松本': 'マツモト', '井上': 'イノウエ',
      '木村': 'キムラ', '林': 'ハヤシ', '清水': 'シミズ', '山崎': 'ヤマザキ',
      '優子': 'ユウコ', '花子': 'ハナコ', '真理': 'マリ', '恵子': 'ケイコ',
      'さくら': 'サクラ', 'あい': 'アイ', 'ゆい': 'ユイ', '陽菜': 'ヒナ',
      '結衣': 'ユイ', '愛': 'アイ', '美紀': 'ミキ', '由美': 'ユミ',
      '麻衣': 'マイ', '彩': 'アヤ', '奈々': 'ナナ', '千尋': 'チヒロ',
      '舞': 'マイ', '瑞希': 'ミズキ', '七海': 'ナナミ', '美咲': 'ミサキ',
      '太郎': 'タロウ', '健太': 'ケンタ', '大輔': 'ダイスケ', '翔': 'ショウ',
      '拓也': 'タクヤ', '悠': 'ユウ', '誠': 'マコト', '翔太': 'ショウタ',
    };

    const parts = name.split(' ');
    const katakana = parts.map(part => kanaMap[part] || part).join(' ');
    return katakana;
  };

  const generatePhone = (customerId: number): string => {
    const prefixes = ['090', '080', '070'];
    const prefix = prefixes[customerId % 3];
    const middle = String(1000 + (customerId * 123) % 9000).padStart(4, '0');
    const last = String(1000 + (customerId * 456) % 9000).padStart(4, '0');
    return `${prefix}-${middle}-${last}`;
  };

  const generateEmail = (name: string, customerId: number): string => {
    const domains = ['gmail.com', 'yahoo.co.jp', 'docomo.ne.jp', 'ezweb.ne.jp', 'softbank.ne.jp'];
    const domain = domains[customerId % domains.length];
    const firstName = name.split(' ')[1] || 'user';
    const lastName = name.split(' ')[0] || 'test';
    return `${firstName.toLowerCase()}.${lastName.toLowerCase()}${customerId}@${domain}`;
  };

  const generateBirthDate = (customerId: number): string => {
    const year = 1970 + (customerId % 30);
    const month = String((customerId % 12) + 1).padStart(2, '0');
    const day = String((customerId % 28) + 1).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const generateTags = (visitCount: number): string[] => {
    const tags: string[] = [];
    if (visitCount >= 3) tags.push('リピーター');
    if (visitCount === 1) tags.push('新規');

    const concerns = ['肩こり', '腰痛', '頭痛', '疲労', '眼精疲労', 'ストレス'];
    const concern = concerns[visitCount % concerns.length];
    tags.push(concern);
    tags.push(`${visitCount}回目`);

    return tags;
  };

  const generateNotes = (visitCount: number): string => {
    const notes = [
      '肩こりがひどい。強めの圧を好む。',
      '腰痛持ち。ゆっくりとした施術を希望。',
      '頭痛持ち。静かな環境を好む。',
      '仕事で疲れている。リラックスを重視。',
      '眼精疲労がひどい。目の周りを重点的に。',
      'ストレスが多い。アロマを好む。',
    ];
    return notes[visitCount % notes.length];
  };

  const generatePreferences = (customerId: number) => {
    const pressures = ['強め', '普通', '弱め', 'やや強め', 'やや弱め'];
    const aromas = ['ラベンダー', 'ローズ', 'ユーカリ', 'ペパーミント', 'ベルガモット', 'なし'];
    const rooms = ['個室希望', '半個室', 'オープンスペース', '指定なし'];

    return {
      pressure: pressures[customerId % pressures.length],
      aroma: aromas[customerId % aromas.length],
      room: rooms[customerId % rooms.length],
    };
  };

  const generateMedicalInfo = (customerId: number) => {
    const allergiesList = [['花粉症'], ['なし'], ['食物アレルギー'], ['金属アレルギー'], []];
    const conditionsList = [
      ['肩こり', '眼精疲労'],
      ['腰痛', '肩こり'],
      ['頭痛'],
      ['ストレス'],
      ['疲労'],
    ];

    return {
      allergies: allergiesList[customerId % allergiesList.length],
      medications: [],
      conditions: conditionsList[customerId % conditionsList.length],
    };
  };

  // Handle customer click to show customer details
  const handleCustomerClick = (reservation: any) => {
    // 実際の顧客データを検索
    const customer = frequentCustomers.find(c => c.id === reservation.customerId);

    if (customer) {
      setSelectedCustomer({
        id: customer.id,
        name: customer.name,
        kana: customer.nameKana || '',
        phone: customer.phone,
        email: customer.email,
        birthDate: customer.birthday || '',
        visitCount: customer.totalVisits,
        totalSpent: customer.totalSpent,
        lastVisit: customer.lastVisit,
        nextReservation: reservation.time + ' ' + reservation.service,
        tags: customer.tags || [],
        notes: customer.preferences || '',
        preferences: {
          pressure: customer.preferences || '',
          aroma: '',
          room: '',
        },
        medicalInfo: customer.medicalInfo || {
          allergies: [],
          medications: [],
          conditions: [],
        }
      });
      setShowCustomerDetail(true);
    } else {
      // フォールバック用のモックデータ（動的生成）
      const mockCustomer = {
        id: reservation.customerId,
        name: reservation.customer,
        kana: generateKana(reservation.customer),
        phone: generatePhone(reservation.customerId),
        email: generateEmail(reservation.customer, reservation.customerId),
        birthDate: generateBirthDate(reservation.customerId),
        visitCount: reservation.visitCount || 1,
        totalSpent: (reservation.visitCount || 1) * (8000 + (reservation.customerId % 7) * 1000),
        lastVisit: '2024-01-10',
        nextReservation: reservation.time + ' ' + reservation.service,
        tags: generateTags(reservation.visitCount || 1),
        notes: generateNotes(reservation.visitCount || 1),
        preferences: generatePreferences(reservation.customerId),
        medicalInfo: generateMedicalInfo(reservation.customerId)
      };

      setSelectedCustomer(mockCustomer);
      setShowCustomerDetail(true);
    }
  };

  // Handle metric card click to navigate to sales page
  const handleMetricClick = (metricType: string) => {
    switch(metricType) {
      case 'sales':
        setSalesActiveTab(0); // 売上トレンドタブ
        setCurrentPage('sales');
        break;
      case 'customers':
        setSalesActiveTab(3); // 時間帯別タブ
        setCurrentPage('sales');
        break;
      case 'spending':
        setSalesActiveTab(1); // メニュー別タブ
        setCurrentPage('sales');
        break;
      case 'repeat':
        setSalesActiveTab(2); // スタッフ別タブ（リピート率に関連）
        setCurrentPage('sales');
        break;
    }
  };

  // Metric cards data - Supabaseから取得したデータを使用（shadcn/ui標準準拠）
  const metricsCards = [
    {
      type: 'sales',
      title: '今月売上',
      value: formatCurrency(metrics.monthlySales),
      icon: DollarSign,
      change: '+8.5%',
      iconColor: 'text-green-600' as const,
    },
    {
      type: 'customers',
      title: '月間来客数',
      value: `${formatNumber(metrics.monthlyCustomers)}名`,
      icon: Users,
      change: '+12.3%',
      iconColor: 'text-blue-600' as const,
    },
    {
      type: 'spending',
      title: '平均客単価',
      value: formatCurrency(metrics.averageSpending),
      icon: TrendingUp,
      change: '+5.2%',
      iconColor: 'text-teal-600' as const,
    },
    {
      type: 'repeat',
      title: 'リピート率',
      value: `${metrics.repeatRate}%`,
      icon: CalendarCheck,
      change: '+2.1%',
      iconColor: 'text-orange-600' as const,
    },
  ];

  // 時間帯による挨拶を取得
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return 'おはようございます';
    if (hour >= 11 && hour < 18) return 'こんにちは';
    return 'お疲れ様です';
  };

  // 本日の予約情報を計算（確定済みのみ表示）
  const activeTodaysReservations = todaysReservations.filter(r => r.status === 'confirmed');
  const frequentCustomersToday = activeTodaysReservations.filter(r => (r.visitCount || 1) >= 10);

  // 活動タイプの定義
  type ActivityType = 'reservation' | 'customer' | 'sales' | 'staff' | 'alert';
  type ActivityPriority = 'critical' | 'high' | 'medium' | 'low';

  interface Activity {
    id: string;
    type: ActivityType;
    priority: ActivityPriority;
    icon: React.ReactNode;
    primary: string;
    secondary: string;
    timestamp: Date;
    isNew: boolean;
    relatedId?: string | number;
    action?: {
      label: string;
      type: string;
    };
  }

  // モック活動データの生成 - Supabaseデータのみを使用（ハードコードデータ削除）
  const getRecentActivities = (forPopup: boolean = false): Activity[] => {
    // Supabaseの活動データのみを使用（ハードコードデータは完全削除）
    const activities: Activity[] = supabaseRecentActivities.map(activity => ({
      id: activity.id,
      type: activity.type as ActivityType,
      priority: activity.priority as ActivityPriority,
      icon: activity.type === 'reservation' ? <CalendarIcon className="text-primary" /> :
            activity.type === 'customer' ? <Users className="text-blue-600" /> :
            activity.type === 'sales' ? <DollarSign className="text-green-600" /> :
            activity.type === 'staff' ? <Star className="text-orange-600" /> :
            <AlertCircle className="text-red-600" />,
      primary: activity.title,
      secondary: activity.description,
      timestamp: activity.timestamp,
      isNew: false,
      relatedId: activity.relatedId,
    }));

    // ポップアップの場合は全データを返す
    if (forPopup) {
      if (activityFilter === 'all') {
        return activities;
      } else if (activityFilter === 'important') {
        return activities.filter(a => a.priority === 'critical' || a.priority === 'high');
      } else {
        return activities.filter(a => a.type === activityFilter);
      }
    }

    // カード表示の場合は3件に制限
    if (activityFilter === 'all') {
      return activities.slice(0, 3);
    } else if (activityFilter === 'important') {
      return activities.filter(a => a.priority === 'critical' || a.priority === 'high').slice(0, 3);
    } else {
      return activities.filter(a => a.type === activityFilter).slice(0, 3);
    }
  };

  // 活動クリック処理
  const handleActivityClick = (activity: Activity) => {
    switch (activity.type) {
      case 'reservation':
        setCurrentPage('reservations');
        break;
      case 'customer':
        if (activity.relatedId) {
          const customerId = typeof activity.relatedId === 'string' ? parseInt(activity.relatedId) : activity.relatedId;
          setSelectedCustomerId(customerId);
          setCurrentPage('customers');
        }
        break;
      case 'sales':
        setCurrentPage('sales');
        break;
      case 'staff':
        setCurrentPage('staff');
        break;
      case 'alert':
        // アラートに応じた画面へ
        if (activity.primary.includes('失客')) {
          setSalesActiveTab(5); // 顧客健康タブ
          setCurrentPage('sales');
        } else if (activity.primary.includes('在庫')) {
          setCurrentPage('inventory');
        }
        break;
    }
  };

  // クイックアクション処理
  const handleQuickAction = (action: { label: string; type: string }) => {
    switch (action.type) {
      case 'fill_slot':
        // 空き枠活用の処理
        alert('空き枠を他の顧客に提案する画面を開きます');
        break;
      case 'send_coupon':
        // クーポン送信処理
        alert('誕生日クーポンを送信します');
        break;
      case 'contact_customer':
        // 顧客連絡処理
        alert('顧客へのフォローメッセージ作成画面を開きます');
        break;
      case 'check_inventory':
        // 在庫確認処理
        setCurrentPage('inventory');
        break;
    }
  };

  // 顧客詳細画面への遷移
  const handleNavigateToCustomerDetail = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setCurrentPage('customers');
  };

  // エラー時の表示
  if (error) {
    return (
      <div className="p-6">
        <Alert className="bg-red-50 text-red-900 border-red-200">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={refreshData}
              className="ml-4"
            >
              再試行
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <>
      {/* Header - shadcn/ui Design System準拠 */}
      <div className="mb-6">
        <h1 className="scroll-m-20 text-2xl font-semibold tracking-tight mb-2">
          {getGreeting()} 👋
        </h1>
        <div className="flex items-center flex-wrap gap-2">
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'short'
            })} ｜ 本日は{loading ? '-' : activeTodaysReservations.length}件の予約 • {loading ? '-' : availableSlots}件の空き時間があります
          </p>
          {!loading && frequentCustomersToday.length > 0 && (
            <Badge variant="default" className="font-medium">
              優良顧客 {frequentCustomersToday.length}名来店
            </Badge>
          )}
        </div>
      </div>

      {/* KPI Metrics Grid - Header直下に移動（shadcn/ui + Framer Motion） */}
      <div className="@container mb-6">
        <div className="grid gap-3 grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-4">
          {metricsCards.map((metric, index) => (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card
                className="cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg h-full"
                onClick={() => handleMetricClick(metric.type)}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {metric.title}
                  </CardTitle>
                  <metric.icon className={`h-4 w-4 ${metric.iconColor}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {loading ? '-' : metric.value}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className={`font-semibold ${metric.iconColor}`}>
                      {metric.change}
                    </span>{' '}
                    前月比
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Interactive Chart Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mb-6"
      >
        <ChartAreaInteractive data={chartData} loading={loading} />
      </motion.div>

      {/* Today's Reservations - 新コンポーネント使用 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="mb-6"
      >
        <TodaysReservations
          reservations={activeTodaysReservations}
          loading={loading}
          onReservationClick={handleCustomerClick}
          selectedDate={selectedDate}
          onDateChange={() => setShowDatePicker(true)}
        />
      </motion.div>

      {/* Recent Activity Feed - Phase 3.1 (shadcn/ui + Framer Motion) */}
      {!loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.8 }}
          className="mb-6"
        >
          <Card className="transition-all hover:-translate-y-0.5 hover:shadow-md">
            <CardHeader className="space-y-1.5 p-6 flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                お知らせ
              </CardTitle>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowActivityFilter(!showActivityFilter)}
                className="h-8 w-8 text-muted-foreground"
                aria-label="フィルター"
              >
                <Filter className="h-4 w-4" aria-hidden="true" />
              </Button>
            </CardHeader>
            <CardContent>
              {/* Filter Options */}
              {showActivityFilter && (
                <div className="mb-4 flex gap-2 flex-wrap">
                  <Badge
                    variant={activityFilter === 'all' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setActivityFilter('all')}
                  >
                    すべて
                  </Badge>
                  <Badge
                    variant={activityFilter === 'reservation' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setActivityFilter('reservation')}
                  >
                    予約
                  </Badge>
                  <Badge
                    variant={activityFilter === 'customer' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setActivityFilter('customer')}
                  >
                    顧客
                  </Badge>
                  <Badge
                    variant={activityFilter === 'sales' ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setActivityFilter('sales')}
                  >
                    売上
                  </Badge>
                  <Badge
                    variant={activityFilter === 'important' ? 'destructive' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setActivityFilter('important')}
                  >
                    重要
                  </Badge>
                </div>
              )}

              <div className="space-y-3">
                {loading ? (
                  <p className="text-muted-foreground">読み込み中...</p>
                ) : getRecentActivities().length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="flex flex-col items-center justify-center gap-3 py-8 text-center"
                >
                  <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                    <Filter className="size-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <div className="grid gap-1">
                    <h3 className="text-sm font-medium">表示する活動がありません</h3>
                    <p className="text-sm text-muted-foreground">
                      フィルターを変更して他の活動を確認してください
                    </p>
                  </div>
                </motion.div>
              ) : (
                  getRecentActivities().map((activity) => {
                    const getIconBgColor = () => {
                      switch(activity.type) {
                        case 'reservation': return 'bg-indigo-50';
                        case 'customer': return 'bg-blue-50';
                        case 'sales': return 'bg-green-50';
                        case 'staff': return 'bg-orange-50';
                        case 'alert': return 'bg-red-50';
                        default: return 'bg-muted/30';
                      }
                    };

                    return (
                      <div
                        key={activity.id}
                        className={`p-4 rounded-lg transition-all cursor-pointer hover:bg-muted/30 hover:translate-x-1 ${
                          activity.isNew ? 'bg-primary/5' : ''
                        } ${
                          activity.priority === 'critical' ? 'border-l-4 border-l-red-500' : ''
                        }`}
                        onClick={() => handleActivityClick(activity)}
                      >
                        <div className="flex gap-4">
                          {/* アイコン背景円 */}
                          <div className={`size-12 rounded-xl ${getIconBgColor()} flex items-center justify-center`}>
                            {activity.icon}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-1 mb-1">
                              <p className={`text-sm ${activity.isNew ? 'font-medium' : ''}`}>
                                {activity.primary}
                              </p>
                              {activity.isNew && (
                                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {activity.secondary}
                            </p>
                          </div>

                          {activity.action && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickAction(activity.action!);
                              }}
                              className="rounded-lg h-9"
                            >
                              {activity.action.label}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <Separator className="my-4" />
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  {getRecentActivities().filter(a => a.isNew).length}件の新着
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllNotifications(true)}
                  className="gap-1.5"
                >
                  すべてのお知らせを見る
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Customer Detail Dialog - 新コンポーネント使用 */}
      <CustomerDetailDialog
        open={showCustomerDetail}
        onOpenChange={setShowCustomerDetail}
        customer={selectedCustomer}
        onNavigateToDetail={handleNavigateToCustomerDetail}
      />

      {/* Date Picker Dialog - shadcn Calendar統合 */}
      <Dialog open={showDatePicker} onOpenChange={setShowDatePicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>日付選択</DialogTitle>
            <DialogDescription>
              表示する日付を選択してください
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setShowDatePicker(false);
                }
              }}
              className="rounded-md border shadow-sm p-6 [--cell-size:3rem] min-w-[350px]"
              classNames={{
                root: "w-full",
              }}
              initialFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDatePicker(false)}>
              キャンセル
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Notifications Dialog - Phase 3.3 (shadcn/ui) */}
      <Dialog open={showAllNotifications} onOpenChange={setShowAllNotifications}>
        <DialogContent className="max-w-2xl flex flex-col">
          <DialogHeader>
            <DialogTitle>すべてのお知らせ</DialogTitle>
            <DialogDescription>
              全ての活動履歴を表示
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[50vh] overflow-y-auto min-h-[200px]">
            {loading ? (
              <p className="text-muted-foreground">読み込み中...</p>
            ) : getRecentActivities(true).length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="flex flex-col items-center justify-center gap-3 py-12 text-center"
                >
                  <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                    <AlertCircle className="size-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <div className="grid gap-1">
                    <h3 className="text-sm font-medium">表示する活動がありません</h3>
                    <p className="text-sm text-muted-foreground">
                      現在、お知らせはありません
                    </p>
                  </div>
                </motion.div>
              ) : (
              getRecentActivities(true).map((activity) => {
                const getIconBgColor = () => {
                  switch(activity.type) {
                    case 'reservation': return 'bg-indigo-50';
                    case 'customer': return 'bg-blue-50';
                    case 'sales': return 'bg-green-50';
                    case 'staff': return 'bg-orange-50';
                    case 'alert': return 'bg-red-50';
                    default: return 'bg-muted/30';
                  }
                };

                return (
                  <div
                    key={activity.id}
                    className={`p-4 rounded-lg transition-all cursor-pointer hover:bg-muted/30 hover:translate-x-1 ${
                      activity.isNew ? 'bg-primary/5' : ''
                    } ${
                      activity.priority === 'critical' ? 'border-l-4 border-l-red-500' : ''
                    }`}
                    onClick={() => {
                      handleActivityClick(activity);
                      setShowAllNotifications(false);
                    }}
                  >
                    <div className="flex gap-4">
                      {/* アイコン背景円 */}
                      <div className={`size-12 rounded-xl ${getIconBgColor()} flex items-center justify-center`}>
                        {activity.icon}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-1 mb-1">
                          <p className={`text-sm ${activity.isNew ? 'font-medium' : ''}`}>
                            {activity.primary}
                          </p>
                          {activity.isNew && (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {activity.secondary}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowAllNotifications(false)}>閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;